import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { User, Mail, Phone, Briefcase, MapPin, Calendar } from 'lucide-react';
import { toast } from '../components/ui/sonner';

const Perfil = () => {
  const { user, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  
  // Inicializar formData com os dados do localStorage se existirem
  const [formData, setFormData] = useState(() => {
    const storedUser = localStorage.getItem('userProfile');
    const profileData = storedUser ? JSON.parse(storedUser) : {};
    
    return {
      nome: user?.nome || 'João Silva',
      email: user?.email || 'joao.silva@infraestruturasdeportugal.pt',
      telefone: profileData.telefone || '+351 912 345 678',
      cargo: user?.cargo || 'Gestor Técnico',
      departamento: profileData.departamento || 'Gestão de Infraestruturas',
      localizacao: profileData.localizacao || 'Lisboa, Portugal',
      dataAdmissao: profileData.dataAdmissao || '2020-03-15'
    };
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = () => {
    // Atualizar o contexto do usuário
    updateProfile({
      nome: formData.nome,
      email: formData.email,
      cargo: formData.cargo
    });
    
    // Guardar dados adicionais no localStorage
    localStorage.setItem('userProfile', JSON.stringify(formData));
    
    toast.success('Perfil atualizado com sucesso!');
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Perfil</h1>
          <p className="text-gray-600 mt-1">Gerir informações pessoais e profissionais</p>
        </div>
        {!isEditing && (
          <Button onClick={() => setIsEditing(true)}>Editar Perfil</Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Card de Avatar */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center">
              <Avatar className="h-32 w-32 mb-4">
                <AvatarImage src={user?.foto} alt={formData.nome} />
                <AvatarFallback className="bg-[#003D7A] text-white text-3xl">
                  {formData.nome?.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <h2 className="text-xl font-bold text-gray-900">{formData.nome}</h2>
              <p className="text-sm text-gray-600">{formData.cargo}</p>
              <p className="text-xs text-gray-500 mt-1">{formData.departamento}</p>
            </div>
          </CardContent>
        </Card>

        {/* Card de Informações Pessoais */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Informações Pessoais</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome" className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-500" />
                  Nome Completo
                </Label>
                <Input
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-500" />
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-500" />
                  Telefone
                </Label>
                <Input
                  id="telefone"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cargo" className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-gray-500" />
                  Cargo
                </Label>
                <Input
                  id="cargo"
                  name="cargo"
                  value={formData.cargo}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="departamento" className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-gray-500" />
                  Departamento
                </Label>
                <Input
                  id="departamento"
                  name="departamento"
                  value={formData.departamento}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="localizacao" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  Localização
                </Label>
                <Input
                  id="localizacao"
                  name="localizacao"
                  value={formData.localizacao}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dataAdmissao" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  Data de Admissão
                </Label>
                <Input
                  id="dataAdmissao"
                  name="dataAdmissao"
                  type="date"
                  value={formData.dataAdmissao}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </div>
            </div>

            {isEditing && (
              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setIsEditing(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSave}>
                  Guardar Alterações
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Estatísticas do Utilizador */}
      <Card>
        <CardHeader>
          <CardTitle>Estatísticas de Utilização</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <p className="text-sm text-gray-600 mb-1">Total de Carregamentos</p>
              <p className="text-2xl font-bold text-blue-600">47</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 border border-green-100">
              <p className="text-sm text-gray-600 mb-1">Energia Consumida</p>
              <p className="text-2xl font-bold text-green-600">1.245 kWh</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
              <p className="text-sm text-gray-600 mb-1">Tempo de Carregamento</p>
              <p className="text-2xl font-bold text-orange-600">98h</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
              <p className="text-sm text-gray-600 mb-1">Locais Acedidos</p>
              <p className="text-2xl font-bold text-purple-600">3</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Perfil;
